import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'

import { toMaskedPhoneNumber } from '@mangovoice/core'
import {
  isPreConnectedStatus,
  PhoneCall,
  PhoneCallStatus,
} from '@mangovoice/types'

export type AnnouncedCall = Pick<
  PhoneCall,
  | 'callUuid'
  | 'status'
  | 'isOnHold'
  | 'isMuted'
  | 'name'
  | 'number'
  | 'direction'
  | 'isPageOrIntercom'
  | 'isUnparked'
  | 'callSource'
>

type Translate = (key: string, options?: Record<string, string>) => string

const isEndedStatus = (status: PhoneCallStatus) =>
  status === PhoneCallStatus.ENDING_CALL || status === PhoneCallStatus.ENDED

const describeIncomingCall = (call: AnnouncedCall, t: Translate) => {
  if (call.isPageOrIntercom) {
    return t('callAnnouncements.incomingCallNameOnly', {
      name: t('popup.callItem.pageIntercom'),
    })
  }
  const name = call.name || t('popup.callItem.unknownCaller')
  const number = call.number ? toMaskedPhoneNumber(call.number) : ''
  if (!number || number === name) {
    return t('callAnnouncements.incomingCallNameOnly', { name })
  }
  return t('callAnnouncements.incomingCall', { name, number })
}

export interface TrackedCall {
  call: AnnouncedCall
  /** Whether this window shows the call, e.g. not hidden by Do Not Disturb. */
  isShown: boolean
}

export type IsCallShown = (call: AnnouncedCall) => boolean

const showEveryCall: IsCallShown = () => true

const trackCalls = (
  calls: readonly AnnouncedCall[],
  isCallShown: IsCallShown = showEveryCall,
): ReadonlyMap<string, TrackedCall> =>
  new Map(
    calls.map((call) => [call.callUuid, { call, isShown: isCallShown(call) }]),
  )

/**
 * Screen reader messages for the differences between two snapshots of the
 * full call list. Only calls this window shows are announced; a call that
 * merely stops being shown (e.g. Do Not Disturb turned on while it rings) is
 * not read as ended. The duration timer is deliberately not call state here.
 */
export const getCallStateAnnouncements = (
  previous: ReadonlyMap<string, TrackedCall>,
  next: ReadonlyMap<string, TrackedCall>,
  t: Translate,
  {
    announceUnansweredRemovals = true,
  }: { announceUnansweredRemovals?: boolean } = {},
): string[] => {
  const messages: string[] = []

  for (const { call, isShown } of next.values()) {
    if (!isShown) continue
    const tracked = previous.get(call.callUuid)

    if (!tracked?.isShown) {
      if (call.direction === 'inbound' && isPreConnectedStatus(call.status)) {
        messages.push(describeIncomingCall(call, t))
      }
      continue
    }

    const before = tracked.call
    if (isEndedStatus(before.status)) continue
    if (isEndedStatus(call.status)) {
      messages.push(t('callAnnouncements.callEnded'))
      continue
    }

    if (
      before.status !== PhoneCallStatus.CONNECTED &&
      call.status === PhoneCallStatus.CONNECTED
    ) {
      messages.push(t('callAnnouncements.connected'))
    }
    if (before.isOnHold !== call.isOnHold) {
      messages.push(
        t(
          call.isOnHold
            ? 'callAnnouncements.onHold'
            : 'callAnnouncements.resumed',
        ),
      )
    }
    if (before.isMuted !== call.isMuted) {
      messages.push(
        t(
          call.isMuted
            ? 'callAnnouncements.muted'
            : 'callAnnouncements.unmuted',
        ),
      )
    }
  }

  for (const [callUuid, { call: before, isShown }] of previous) {
    if (
      isShown &&
      !next.has(callUuid) &&
      !isEndedStatus(before.status) &&
      (announceUnansweredRemovals || !isPreConnectedStatus(before.status))
    ) {
      messages.push(t('callAnnouncements.callEnded'))
    }
  }

  return messages
}

interface CallStateAnnouncementOptions {
  /** Which calls this window shows; others are never announced. */
  isCallShown?: IsCallShown
  /**
   * Whether a ringing call that drops out of the list is read as ended. Turn
   * this off where calls can vanish for other reasons, e.g. the call-pop's
   * shared list drops ringing calls as soon as Do Not Disturb turns on.
   */
  announceUnansweredRemovals?: boolean
}

/**
 * Announces incoming, connected, hold, resume, mute, unmute and ended
 * transitions through the shared polite status region. Pass the full call
 * list and say which calls this window shows with `isCallShown`, so a call
 * leaving the shown set isn't mistaken for one that ended. Changes that land
 * in the same update are read as one message.
 */
export const useCallStateAnnouncements = (
  calls: readonly AnnouncedCall[],
  {
    isCallShown,
    announceUnansweredRemovals,
  }: CallStateAnnouncementOptions = {},
) => {
  const { t } = useTranslation()
  const announce = useAnnounce()
  const previousRef = useRef<ReadonlyMap<string, TrackedCall>>(new Map())

  useEffect(() => {
    const next = trackCalls(calls, isCallShown)
    const messages = getCallStateAnnouncements(
      previousRef.current,
      next,
      t as Translate,
      { announceUnansweredRemovals },
    )
    previousRef.current = next
    if (messages.length > 0) announce(messages.join('. '))
  }, [calls, isCallShown, announceUnansweredRemovals, t, announce])
}
